import { Menu } from "@design-system/Menu/Menu.lib";
import { EntitySettingsPages } from "@components/settings/constants";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { ACCOUNTS_POSSIBLY_PERSONAL } from "@common/helpers/financialAccount";
import { getNonFixablePlaidConnectionError } from "@common/helpers/financialConnection";
import {
    canReconcileFinancialAccount,
    financialAccountIsErrored,
} from "@helpers/financialAccount";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import React, { useCallback, useState } from "react";
import { FinancialAccount } from "@common/types/financialAccount";
import { FinancialConnection } from "@common/types/financialConnection";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { useOverlays } from "contexts/OverlayContext";
import { MergeAccountsModal } from "@components/settings/Accounts/MergeAccounts/MergeAccountsModal";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { HStack } from "@design-system/HStack/HStack";
import { StandardModal } from "@design-system/Modal/Modal";
import { AccountTypeChange } from "@components/settings/Accounts/AccountDetails/AccountTypeChange";
import { getEntityPaneKey } from "@components/settings/lib";
import { useSettings } from "@components/settings/SettingsProvider";
import { useFinancialAccountRemovalMutation } from "@mutations/financialAccount";
import { FinancialAccountStateManager } from "./FinancialAccountStateManager";
import { FixConnectionMenuItem } from "./FixConnectionMenuItem";
import styles from "./FinancialAccountManagementDropdown.module.scss";

interface Props {
    connection?: FinancialConnection | IntegrationConnection;
    account: FinancialAccount;
    allowRemove?: boolean;
}

export const FinancialAccountManagementDropdown: React.FC<Props> = ({
    account,
    connection,
    allowRemove,
}) => {
    const { open } = useSettings();
    const { openOverlay } = useOverlays();
    const { mergingAccounts } = useTypedFlags();
    const removeAccount = useFinancialAccountRemovalMutation(account);
    const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
    const { hasAccess: hasImporterAccess } = useAllowFeature(
        PREMIUM_FEATURES.IMPORTER,
    );
    const { hasAccess: hasReconciliationAccess } = useAllowFeature(
        PREMIUM_FEATURES.RECONCILIATION,
    );

    const entity = account.entity;
    const canBePersonal = ACCOUNTS_POSSIBLY_PERSONAL[account.sourceType];
    const canReconcile = canReconcileFinancialAccount(account);

    const hasSettingsGroup = canBePersonal || !!entity;
    const hasBottomGroup =
        (mergingAccounts && !!entity) || !!connection || allowRemove;

    const { refuseIfViewOnly } = useViewOnlyRefusal();

    // Refused at the click rather than at the upload: the importer would
    // otherwise open, take a file and parse it before the server said no
    // (PRO-11392).
    const handleImportTransactions = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        openOverlay("importer", true);
    }, [openOverlay, refuseIfViewOnly]);

    const handleReconcileTransactions = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        openOverlay("reconcilieFinancialAccount", {
            financialAccountId: account.id,
        });
    }, [openOverlay, refuseIfViewOnly, account.id]);

    const openSettings = (subPath?: string) => {
        if (!entity) {
            return;
        }
        open([
            getEntityPaneKey(EntitySettingsPages.SETTINGS, entity.id),
            account.id.toString(),
            ...(subPath ? [subPath] : []),
        ]);
    };

    return (
        <>
            <Menu.More>
                {/* 1. Import transactions */}
                {entity && hasImporterAccess && (
                    <Menu.Item onClick={handleImportTransactions}>
                        Import transactions
                    </Menu.Item>
                )}

                {/* 2. Reconcile transactions */}
                {canReconcile && hasReconciliationAccess && (
                    <Menu.Item onClick={handleReconcileTransactions}>
                        Reconcile transactions
                    </Menu.Item>
                )}

                {hasSettingsGroup && <Menu.Separator />}

                {/* 4. Account type */}
                {canBePersonal && (
                    <HStack
                        gap={8}
                        align="center"
                        justify="between"
                        paddingInline={8}
                        paddingBlock={4}
                    >
                        <span className={styles.accountTypeLabel}>
                            Account type
                        </span>
                        <CaptureClicks>
                            <AccountTypeChange
                                account={account}
                                connection={connection}
                                showLabel={false}
                                size="sm"
                            />
                        </CaptureClicks>
                    </HStack>
                )}

                {/* 5. Edit account name */}
                {entity && (
                    <Menu.Item onClick={() => openSettings("name")}>
                        Edit account name
                    </Menu.Item>
                )}

                {/* 6. Update start date */}
                {entity && (
                    <Menu.Item onClick={() => openSettings("startDate")}>
                        Update start date
                    </Menu.Item>
                )}

                {/* 7. Update end date */}
                {entity && (
                    <Menu.Item onClick={() => openSettings("endDate")}>
                        Update end date
                    </Menu.Item>
                )}

                {/* 8. Settings */}
                {entity && (
                    <Menu.Item onClick={() => openSettings()}>
                        Settings
                    </Menu.Item>
                )}

                {hasBottomGroup && <Menu.Separator />}

                {/* 10. Merge account */}
                {mergingAccounts && entity && (
                    <Menu.Item onClick={() => setIsMergeModalOpen(true)}>
                        Merge account
                    </Menu.Item>
                )}

                {connection &&
                    account.disabled &&
                    financialAccountIsErrored(account) &&
                    getNonFixablePlaidConnectionError(connection) == null && (
                        <FixConnectionMenuItem
                            account={account}
                            connection={connection}
                        />
                    )}

                {/* 11. Disable/enable account */}
                {connection && (
                    <FinancialAccountStateManager
                        account={account}
                        connection={connection}
                    />
                )}

                {/* 12. Remove account */}
                {allowRemove && (
                    <Menu.Item
                        destructive
                        onClick={() => removeAccount.mutate({})}
                        tooltip="If you remove this account you will lose all previous transactions."
                    >
                        Remove account
                    </Menu.Item>
                )}
            </Menu.More>

            {mergingAccounts && entity && (
                <StandardModal
                    show={isMergeModalOpen}
                    onHide={() => setIsMergeModalOpen(false)}
                    size="xl"
                >
                    <MergeAccountsModal
                        financialAccount={account}
                        entity={entity}
                        close={() => setIsMergeModalOpen(false)}
                    />
                </StandardModal>
            )}
        </>
    );
};
